import { AnalyticsContext } from '@carvana/eventable-clickstream';
import React, {
  PropsWithChildren,
  createContext,
  useState,
  useCallback,
  useContext
} from 'react';

enum Modals {
  TradeModal = 'trade-modal',
  CancelOrderModal = 'cancel-order-modal',
  EditOrderModal = 'edit-order-modal',
  CardEntryModal = 'card-entry-modal',
  LinkPlaidModal = 'link-plaid-modal',
  ExplainPlaidModal = 'explain-plaid-modal',
  BankAccountManualEntryModal = 'bank-account-manual-entry-modal',
  DownPaymentCreditModal = 'down-payment-credit-modal',
  DeclineAutoPayModal = 'decline-auto-pay-modal',
  DLViewModal = 'view-modal',
  PersonaConnectingModal = 'connection-modal',
  PersonaConnectingErrorModal = 'connection-error-modal',
  NameMatchingModal = 'name-matching-modal',
  CustomizeTermsModal = 'customize-terms-modal',
  BuildDealTradeInTaxSavingsModal = 'build-deal-trade-in-tax-savings-modal',
  CoSignerModal = 'cosigner-modal',
  PrimaryFraudModal = 'primary-fraud-modal',
  CosignerFraudModal = 'cosigner-fraud-modal',
  CoApplicantUnableToAddModal = 'co-applicant-unable-to-add-modal'
}

const defaultValue = {
  currentModal: null as null | Modals,
  isTradeModalShown: false,
  isCancelOrderModalShown: false,
  isEditOrderModalShown: false,
  isCardEntryModalShown: false,
  isLinkPlaidModalShown: false,
  isExplainPlaidModalShown: false,
  isBankAccountManualEntryModalShown: false,
  isDownPaymentCreditModalShown: false,
  isDeclineAutoPayModalShown: false,
  isDLViewModalShown: false,
  isPersonaConnectingModalShown: false,
  isPersonaConnectingErrorModalShown: false,
  isNameMatchingModalShown: false,
  isCustomizeTermsModalShown: false,
  isBuildDealTradeInTaxSavingsModalShown: false,
  isCosignerModalShown: false,
  isPrimaryFraudModalShown: false,
  isCosignerFraudModalShown: false,
  isCoApplicantUnableToAddModalShown: false,
  setTradeInModalOpen: (_: boolean) => {},
  setCancelOrderModalOpen: (_: boolean) => {},
  setEditOrderModalOpen: (_: boolean) => {},
  setCardEntryModalOpen: (_: boolean) => {},
  setLinkPlaidModalOpen: (_: boolean) => {},
  setExplainPlaidModalOpen: (_: boolean) => {},
  setBankAccountManualEntryModalOpen: (_: boolean) => {},
  setDownPaymentCreditModalOpen: (_: boolean) => {},
  setDeclineAutoPayModalOpen: (_: boolean) => {},
  setDLViewModalOpen: (_: boolean) => {},
  setPersonaConnectingModalOpen: (_: boolean) => {},
  setPersonaConnectingErrorModalOpen: (_: boolean) => {},
  setNameMatchingModalOpen: (_: boolean) => {},
  setCustomizeTermsModalOpen: (_: boolean) => {},
  setBuildDealTradeInTaxSavingsModalOpen: (_: boolean) => {},
  setCosignerModalOpen: (_: boolean) => {},
  setPrimaryFraudModalOpen: (_: boolean) => {},
  setCosignerFraudModalOpen: (_: boolean) => {},
  setCoApplicantUnableToAddModalOpen: (_: boolean) => {}
};

const ModalContext = createContext(defaultValue);

const ModalContextProvider: React.FC<PropsWithChildren<{}>> = ({
  children
}) => {
  const [currentModal, setCurrentModal] = useState(null as null | Modals);
  const { trackEvent } = useContext(AnalyticsContext);

  const closeModal = useCallback(() => {
    trackEvent('pp_modal_closed', { modal: currentModal });
    setCurrentModal(null);
  }, [setCurrentModal, trackEvent, currentModal]);

  const showModal = useCallback(
    (modal: Modals) => {
      if (currentModal) {
        // there's already a modal showing, we shouldn't let this happen;
        console.error(
          `Attempted to show ${modal} while ${currentModal} is open`
        );
        closeModal();
      }
      trackEvent('pp_modal_opened', { modal });
      setCurrentModal(modal);
    },
    [closeModal, setCurrentModal, currentModal, trackEvent]
  );

  const setTradeInModalOpen = useCallback(
    (isOpen: boolean) => (isOpen ? showModal(Modals.TradeModal) : closeModal()),
    [showModal, closeModal]
  );

  const setCancelOrderModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.CancelOrderModal) : closeModal(),
    [showModal, closeModal]
  );

  const setEditOrderModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.EditOrderModal) : closeModal(),
    [showModal, closeModal]
  );

  const setCardEntryModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.CardEntryModal) : closeModal(),
    [showModal, closeModal]
  );

  const setLinkPlaidModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.LinkPlaidModal) : closeModal(),
    [showModal, closeModal]
  );

  const setExplainPlaidModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.ExplainPlaidModal) : closeModal(),
    [showModal, closeModal]
  );

  const setBankAccountManualEntryModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.BankAccountManualEntryModal) : closeModal(),
    [showModal, closeModal]
  );

  const setDownPaymentCreditModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.DownPaymentCreditModal) : closeModal(),
    [showModal, closeModal]
  );

  const setDeclineAutoPayModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.DeclineAutoPayModal) : closeModal(),
    [showModal, closeModal]
  );

  const setDLViewModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.DLViewModal) : closeModal(),
    [showModal, closeModal]
  );

  const setPersonaConnectingModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.PersonaConnectingModal) : closeModal(),
    [showModal, closeModal]
  );

  const setPersonaConnectingErrorModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.PersonaConnectingErrorModal) : closeModal(),
    [showModal, closeModal]
  );

  const setNameMatchingModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.NameMatchingModal) : closeModal(),
    [showModal, closeModal]
  );

  const setCustomizeTermsModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.CustomizeTermsModal) : closeModal(),
    [showModal, closeModal]
  );

  const setBuildDealTradeInTaxSavingsModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.BuildDealTradeInTaxSavingsModal) : closeModal(),
    [showModal, closeModal]
  );

  const setCosignerModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.CoSignerModal) : closeModal(),
    [showModal, closeModal]
  );

  const setPrimaryFraudModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.PrimaryFraudModal) : closeModal(),
    [showModal, closeModal]
  );

  const setCosignerFraudModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.CosignerFraudModal) : closeModal(),
    [showModal, closeModal]
  );

  const setCoApplicantUnableToAddModalOpen = useCallback(
    (isOpen: boolean) =>
      isOpen ? showModal(Modals.CoApplicantUnableToAddModal) : closeModal(),
    [showModal, closeModal]
  );

  return (
    <ModalContext.Provider
      value={{
        currentModal,
        isTradeModalShown: currentModal === Modals.TradeModal,
        isCancelOrderModalShown: currentModal === Modals.CancelOrderModal,
        isEditOrderModalShown: currentModal === Modals.EditOrderModal,
        isCardEntryModalShown: currentModal === Modals.CardEntryModal,
        isLinkPlaidModalShown: currentModal === Modals.LinkPlaidModal,
        isExplainPlaidModalShown: currentModal === Modals.ExplainPlaidModal,
        isBankAccountManualEntryModalShown:
          currentModal === Modals.BankAccountManualEntryModal,
        isDownPaymentCreditModalShown:
          currentModal === Modals.DownPaymentCreditModal,
        isDeclineAutoPayModalShown: currentModal === Modals.DeclineAutoPayModal,
        isDLViewModalShown: currentModal === Modals.DLViewModal,
        isPersonaConnectingModalShown:
          currentModal === Modals.PersonaConnectingModal,
        isPersonaConnectingErrorModalShown:
          currentModal === Modals.PersonaConnectingErrorModal,
        isNameMatchingModalShown: currentModal === Modals.NameMatchingModal,
        isCustomizeTermsModalShown: currentModal === Modals.CustomizeTermsModal,
        isBuildDealTradeInTaxSavingsModalShown:
          currentModal === Modals.BuildDealTradeInTaxSavingsModal,
        isCosignerModalShown: currentModal === Modals.CoSignerModal,
        isPrimaryFraudModalShown: currentModal === Modals.PrimaryFraudModal,
        isCosignerFraudModalShown: currentModal === Modals.CosignerFraudModal,
        isCoApplicantUnableToAddModalShown:
          currentModal === Modals.CoApplicantUnableToAddModal,
        setTradeInModalOpen,
        setCancelOrderModalOpen,
        setEditOrderModalOpen,
        setCardEntryModalOpen,
        setLinkPlaidModalOpen,
        setExplainPlaidModalOpen,
        setBankAccountManualEntryModalOpen,
        setDownPaymentCreditModalOpen,
        setDeclineAutoPayModalOpen,
        setDLViewModalOpen,
        setPersonaConnectingModalOpen,
        setPersonaConnectingErrorModalOpen,
        setNameMatchingModalOpen,
        setCustomizeTermsModalOpen,
        setBuildDealTradeInTaxSavingsModalOpen,
        setCosignerModalOpen,
        setPrimaryFraudModalOpen,
        setCosignerFraudModalOpen,
        setCoApplicantUnableToAddModalOpen
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export default ModalContext;
export { ModalContextProvider };
